import { useDispatch, useSelector } from "react-redux";

import { walletLabelMapping } from "../../constants/walletMenuOptions";
import { useCallback, useEffect, useState } from "react";
import { config } from "../../config/hedera";
import {
  updateWalletData,
  clearWalletData,
  clearUserData,
  updateStakeData,
  clearStakeData,
  updateUserData,
} from "store";
import { EVENTS, TRANSACTION_TYPE } from "../../constants/events";

import {
  formatErrorData,
  getErrorData,
  getFormatedResponse,
  getMaintainanceData,
} from "../utils";

import {
  TOKENTYPE,
  useStake,
  useUnstake,
  useWithdraw,
  useAllowance,
  useAssociate,
  useLimits,
} from "@stader-labs/web-sdk"; // from "@stader-labs/web-sdk"

import { trackEvent } from "../../utils/firebase";
import {
  MAX_STAKE,
  STAKE_CTA,
  STAKE_FAILED,
  TOKENX_APPROVE_FAILED,
  TOKEN_APPROVE_CTA,
  TOKEN_APPROVE_FAILED,
  UNSTAKE_CTA,
  UNSTAKE_FAILED,
  WITHDRAWAL_FAILED,
  WALLET_CONNECTION_FAILED,
  TOKEN_ASSOCIATE_CTA,
  TOKEN_ASSOCIATE_FAILED,
  TOKENX_ASSOCIATE_FAILED,
} from "../../constants/firebase";
import { TXN_TYPES } from "../../constants/transactions";
import TransactionModal from "../../components/TransactionModal";
import { DAYS_TO_WAIT_FOR_WITHDRAW } from "../../constants/constants";
import { sentryErrorLog } from "../../utils/sentryLog";
import { useToast, Text } from "@chakra-ui/react";
import Icon from "../../components/Icon";
import { ErrorToast, Warning as WarningIcon } from "icons";
import useQueryReferral from "../../hooks/useQueryReferral";
import { useHederaWalletConnect } from "./wallet-connect";
import { useQueryClient } from "@tanstack/react-query";
import { useMetamask } from "./metamask";
import {
  approveHederaEvmToken,
  associateHederaEvmToken,
  getProvider,
  stakeHederaEvm,
  unstakeHederaEvm,
  withdrawHederaEvm,
} from "./metamask/evm-calls";
import { ContractId, TokenId } from "@hashgraph/sdk";

const WALLET_CONNECT = "walletconnect";
const METAMASK = "metamask";

const token = process.env.NEXT_PUBLIC_TOKEN || "";
const network = process.env.NEXT_PUBLIC_NETWORK || "mainnet";

interface WalletConnector {
  name: string;
  id: string;
  inInstalled: boolean;
}

const ServiceProvider = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { withdraw: withdrawToken } = useWithdraw();
  const { approve: approveToken } = useAllowance();

  const { stake: stakeToken } = useStake();
  const { unstake: unstakeToken } = useUnstake();
  const { associateToken } = useAssociate();
  const { fetchMinMaxLimit } = useLimits();
  const stakeReferralId = useQueryReferral();

  const { emitter } = useSelector((state: any) => state.event);
  const { stakeAmount, unstakeAmount, txnLoader, tokenXAllowence } =
    useSelector((state: any) => state.stake);
  const { walletAddress, connectorID } = useSelector(
    (state: any) => state.wallet
  );
  const { tokenAmount, tokenXAmount, isNativeChain } = useSelector(
    (state: any) => state.user
  );

  const handleWalletDisconnect = useCallback(
    (type: "session_delete" | "session_expire") => {
      if (type === "session_expire") {
        toast({
          description: (
            <Text fontWeight={600} fontSize="16px">
              Your wallet connect session has expired
            </Text>
          ),
          status: "info",
          icon: <Icon Icon={WarningIcon} width="24px" height="24px" />,
          duration: null,
          position: "top",
          isClosable: true,
        });
      }
    },
    [toast]
  );

  const {
    init: initWC,
    connect: connectWC,
    disconnect: disconnectWC,
    wcTopic,
    signer,
  } = useHederaWalletConnect({
    projectId: process.env.NEXT_PUBLIC_WC_PROJECT_ID ?? "",
    hbarxId: config.ids.tokenId,
    onDisconnect: handleWalletDisconnect,
  });

  const { connect: connectMM } = useMetamask();

  const [persistantData, setPersistantData] = useState<any>({});

  const fetchLimits = async () => {
    try {
      const data: any = await fetchMinMaxLimit();
      dispatch(updateStakeData({ limits: data }));
    } catch (err) {
      sentryErrorLog(err);
    }
  };

  useEffect(() => {
    dispatch(
      updateUserData({
        tokenSymbol: "HBAR",
        tokenXSymbol: "HBARX",
      })
    );
    fetchLimits();
  }, []);

  useEffect(() => {
    initWC();
    const connectors: WalletConnector[] = [
      {
        name: "Wallet Connect",
        id: WALLET_CONNECT,
        inInstalled: true,
      },
      {
        name: "MetaMask",
        id: METAMASK,
        inInstalled: true,
      },
    ];
    dispatch(
      updateWalletData({
        connectors,
      })
    );
    dispatch(
      updateStakeData({
        blockExplorerURL: config.blockExplorerURL,
      })
    );
  }, [dispatch, initWC]);

  const connect = async (connector: WalletConnector) => {
    if (connector.id === METAMASK) {
      try {
        const { type, account } = await connectMM();

        if (type === "success") {
          if (!account) throw new Error("No account connected");

          dispatch(
            updateWalletData({
              connectorID: METAMASK,
              walletName: "MetaMask",
              isConnected: true,
              walletAddress: account.evm,
              walletAddressHedera: account.hedera,
            })
          );

          return;
        }

        if (type === "error") {
          toast({
            description: (
              <Text fontWeight={600} fontSize="16px">
                Error connecting to Metamask, please try again
              </Text>
            ),
            status: "error",
            icon: <Icon Icon={ErrorToast} width="24px" height="24px" />,
            duration: 3000,
            position: "top",
            isClosable: true,
          });

          return;
        }

        const message =
          type === "no-metamask"
            ? "Metamask extension is not found, please install"
            : "Your account has not been created on the Hedera Network yet. Please transfer some HBAR to your metamask address to initiate the account creation process";

        toast({
          description: (
            <Text fontWeight={600} fontSize="16px">
              {message}
            </Text>
          ),
          status: "info",
          icon: <Icon Icon={WarningIcon} width="24px" height="24px" />,
          duration: 6000,
          position: "top",
          isClosable: true,
        });
      } catch (error: any) {
        trackEvent(WALLET_CONNECTION_FAILED, {
          wallet_name: walletLabelMapping.metamask,
          reason: error.message,
        });
      }
    } else if (connector.id === WALLET_CONNECT) {
      try {
        const accountId = await connectWC();

        if (!accountId) throw new Error("No account connected");

        dispatch(
          updateWalletData({
            connectorID: WALLET_CONNECT,
            walletName: "Wallet Connect",
            isConnected: true,
            walletAddress: accountId,
          })
        );
      } catch (error: any) {
        trackEvent(WALLET_CONNECTION_FAILED, {
          wallet_name: walletLabelMapping.walletconnect,
          reason: error.message,
        });
      }
    }
  };

  const disconnect = () => {
    if (connectorID === METAMASK) {
      toast({
        description: (
          <Text fontWeight={600} fontSize="16px">
            Please disconnect using the wallet browser extension popup to
            prevent auto reconnect
          </Text>
        ),
        status: "info",
        icon: <Icon Icon={WarningIcon} width="24px" height="24px" />,
        duration: 3000,
        position: "top",
        isClosable: true,
      });
    } else {
      disconnectWC();
    }
  };

  // TODO: use get allowance query

  const checkMaintainance = async () => {
    const id = "maintainance";
    const maintainanceData = await getMaintainanceData();
    if (maintainanceData?.isActive && !toast.isActive(id)) {
      toast({
        id,
        description: (
          <Text fontWeight={600} fontSize="16px">
            Transactions unavailable: Sync with protocol staking rewards in
            progress
          </Text>
        ),
        status: "error",
        icon: <Icon Icon={ErrorToast} width="24px" height="24px" />,
        duration: 5000,
        position: "top",
        isClosable: false,
      });
    }
    return maintainanceData?.isActive;
  };

  const startTransaction = (transactionType: string) => {
    setPersistantData((prev: any) => ({
      ...prev,
      hasUserDenied: false,
      isTxnProcessing: true,
      transactionType: transactionType,
    }));
    dispatch(
      updateStakeData({
        isUnstaking: transactionType === TXN_TYPES.UNSTAKE,
        isStaking: transactionType === TXN_TYPES.STAKE,
        txnLoader: true,
      })
    );
  };

  const endTransaction = () => {
    dispatch(
      updateStakeData({
        isUnstaking: false,
        isStaking: false,
      })
    );
    setPersistantData((prev: any) => ({
      ...prev,
      isTxnProcessing: false,
    }));

    queryClient.invalidateQueries({
      queryKey: ["dapp-sdk"],
    });
  };

  // Aborts before the wallet is prompted, so close the transaction modal too
  const cancelTransaction = () => {
    dispatch(
      updateStakeData({
        isUnstaking: false,
        isStaking: false,
        txnLoader: false,
        approveTokenLoading: false,
        approveTokenXLoading: false,
      })
    );
    setPersistantData({ isTxnProcessing: false });
  };

  const getErrorMessage = (err: any, errorMessage: string) => {
    let parsed: any = null;

    try {
      // Remove prefix and parse JSON
      const jsonString = err.message.replace(
        /^Error executing transaction or query:\s*/,
        ""
      );
      parsed = JSON.parse(jsonString);
    } catch {
      return errorMessage || err?.message;
    }

    const errType = parsed?.txError?.message ?? parsed?.queryError?.message;

    if (errType) {
      if (errType === "INSUFFICIENT_ALLOWANCE") {
        return "Insufficient Allowance";
      }

      if (errType === "INSUFFICIENT_BALANCE") {
        return "Insufficient Balance";
      }

      if (errType === "USER_REJECT") {
        return "User Rejected";
      }
    }

    return errorMessage || err?.message;
  };

  const errorTransaction = (error: any, errorMessage: string) => {
    sentryErrorLog(error);

    setPersistantData((prev: any) => ({
      ...prev,
      hasUserDenied: formatErrorData(error),
      error: getErrorMessage(error, errorMessage),
    }));
  };

  const handleStake = async () => {
    startTransaction(TXN_TYPES.STAKE);

    const isUnderMaintainance = await checkMaintainance();
    if (isUnderMaintainance) {
      cancelTransaction();
      return;
    }

    trackEvent(STAKE_CTA, {
      stake_amount_entered: parseFloat(stakeAmount),
    });

    if (+stakeAmount === tokenAmount) {
      trackEvent(MAX_STAKE, {
        stake_amount_entered: parseFloat(stakeAmount),
      });
    }

    if (connectorID === METAMASK) {
      return stakeHederaEvm(
        stakeAmount,
        ContractId.fromString(config.ids.stakingContractId)
      )
        .then(async (hash: string | null) => {
          if (hash == null) {
            throw new Error("Hash is null");
          }

          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: hash || "",
          }));

          const provider = getProvider();

          if (!provider) {
            throw new Error(
              "[EDGECASE]: No provider found; should never happen"
            );
          }

          const receipt = await provider.waitForTransaction(hash, 1);

          if (receipt.status !== 1) {
            throw new Error("Transaction failed");
          }

          dispatch(updateStakeData({ isStaking: false, stakeAmount: "" }));
        })
        .catch((error: any) => {
          errorTransaction(error, "staking failed");
          trackEvent(STAKE_FAILED, {
            stake_amount_entered: parseFloat(stakeAmount),
            reason: getErrorMessage(error, "staking failed"),
          });
        })
        .finally(() => {
          endTransaction();
        });
    }

    if (wcTopic == null) {
      return;
    }

    stakeToken(
      walletAddress,
      signer,
      stakeAmount,
      {
        topic: wcTopic,
        isNativeChain,
        connectorID,
      },
      stakeReferralId
    )
      .then(async (response: any) => {
        const res = await getFormatedResponse(response);
        if (res.success) {
          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: res?.response?.transactionHash || "",
          }));
        } else {
          setPersistantData((prev: any) => ({
            ...prev,
            ...getErrorData(res),
          }));
        }
        dispatch(updateStakeData({ isStaking: false, stakeAmount: "" }));
      })
      .catch((error: any) => {
        errorTransaction(error, "staking failed");
        trackEvent(STAKE_FAILED, {
          stake_amount_entered: parseFloat(stakeAmount),
          reason: getErrorMessage(error, "staking failed"),
        });
      })
      .finally(() => {
        endTransaction();
      });
  };

  const handleUnStake = async () => {
    startTransaction(TXN_TYPES.UNSTAKE);

    const isUnderMaintainance = await checkMaintainance();
    if (isUnderMaintainance) {
      cancelTransaction();
      return;
    }

    trackEvent(UNSTAKE_CTA, {
      unstake_amount_entered: parseFloat(unstakeAmount),
    });
    if (+unstakeAmount === tokenXAmount) {
      trackEvent(MAX_STAKE, {
        unstake_amount_entered: parseFloat(unstakeAmount),
      });
    }
    startTransaction(TXN_TYPES.UNSTAKE);

    if (connectorID === METAMASK) {
      return unstakeHederaEvm(
        unstakeAmount,
        ContractId.fromString(config.ids.stakingContractId)
      )
        .then(async (hash: string | null) => {
          if (hash == null) {
            throw new Error("Hash is null");
          }

          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: hash,
          }));

          const provider = getProvider();

          if (!provider) {
            throw new Error(
              "[EDGECASE]: No provider found; should never happen"
            );
          }

          const receipt = await provider.waitForTransaction(hash, 1);

          if (receipt.status !== 1) {
            throw new Error("Transaction failed");
          }

          dispatch(updateUserData({ approvedTokenX: false }));

          dispatch(updateStakeData({ isUnstaking: false, unstakeAmount: "" }));
        })
        .catch((error: any) => {
          errorTransaction(error, "unstaking failed");
          trackEvent(UNSTAKE_FAILED, {
            unstake_amount_entered: parseFloat(unstakeAmount),
            reason: getErrorMessage(error, "unstaking failed"),
          });
        })
        .finally(() => {
          endTransaction();
        });
    }

    if (wcTopic == null) {
      return;
    }
    unstakeToken(
      walletAddress,
      signer,
      unstakeAmount,
      {
        topic: wcTopic,
        isNativeChain,
        connectorID,
      },
      stakeReferralId
    )
      .then(async (response: any) => {
        const res = await getFormatedResponse(response);
        if (res.success) {
          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: res?.response?.transactionHash || "",
          }));
          dispatch(updateUserData({ approvedTokenX: false }));
        } else {
          setPersistantData((prev: any) => ({
            ...prev,
            ...getErrorData(res),
          }));
        }
        dispatch(updateStakeData({ isUnstaking: false, unstakeAmount: "" }));
      })
      .catch((error: any) => {
        errorTransaction(error, "unstaking failed");
        trackEvent(UNSTAKE_FAILED, {
          unstake_amount_entered: parseFloat(unstakeAmount),
          reason: getErrorMessage(error, "unstaking failed"),
        });
      })
      .finally(() => {
        endTransaction();
      });
  };

  const handleClaim = async (transaction: any) => {
    startTransaction(TXN_TYPES.WITHDRAW);

    const isUnderMaintainance = await checkMaintainance();
    if (isUnderMaintainance) {
      cancelTransaction();
      return;
    }

    trackEvent(UNSTAKE_CTA, {
      unstake_amount_entered: parseFloat(transaction.amount),
    });

    startTransaction(TXN_TYPES.WITHDRAW);

    if (connectorID === METAMASK) {
      return withdrawHederaEvm(
        transaction.id,
        ContractId.fromString(config.ids.undelegationContractId)
      )
        .then(async (hash: string | null) => {
          if (hash == null) {
            throw new Error("Hash is null");
          }

          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: hash || "",
          }));

          const provider = getProvider();

          if (!provider) {
            throw new Error(
              "[EDGECASE]: No provider found; should never happen"
            );
          }

          const receipt = await provider.waitForTransaction(hash, 1);

          if (receipt.status !== 1) {
            throw new Error("Transaction failed");
          }
        })
        .catch((error: any) => {
          errorTransaction(error, "withdrawal failed");
          trackEvent(WITHDRAWAL_FAILED, {
            reason: getErrorMessage(error, "withdrawal failed"),
          });
        })
        .finally(() => {
          endTransaction();
        });
    }

    if (wcTopic == null) {
      return;
    }

    withdrawToken(
      walletAddress,
      signer,
      transaction.id,
      {
        topic: wcTopic,
        isNativeChain,
        connectorID,
      },
      transaction.oldContract
    )
      .then(async (response: any) => {
        const res = await getFormatedResponse(response);
        if (res.success) {
          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: res?.response?.transactionHash || "",
          }));
        } else {
          setPersistantData((prev: any) => ({
            ...prev,
            ...getErrorData(res),
          }));
        }
      })
      .catch((error: any) => {
        errorTransaction(error, "withdrawal failed");
        trackEvent(WITHDRAWAL_FAILED, {
          reason: getErrorMessage(error, "withdrawal failed"),
        });
      })
      .finally(() => {
        endTransaction();
      });
  };

  const handleTokenAssociate = async (
    transaction: any,
    tokenType: TOKENTYPE
  ) => {
    startTransaction(TXN_TYPES.XTOKEN_ASSOCIATE);

    const isUnderMaintainance = await checkMaintainance();
    if (isUnderMaintainance) {
      cancelTransaction();
      return;
    }

    trackEvent(TOKEN_ASSOCIATE_CTA);

    if (connectorID === METAMASK) {
      return associateHederaEvmToken(TokenId.fromString(config.ids.tokenId))
        .then(async (hash: string | null) => {
          if (hash == null) {
            throw new Error("Hash is null");
          }

          if (tokenType === TOKENTYPE.TOKENX) {
            dispatch(
              updateStakeData({
                tokenXAssociated: true,
              })
            );
          }
          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: hash || "",
          }));

          const provider = getProvider();

          if (!provider) {
            throw new Error(
              "[EDGECASE]: No provider found; should never happen"
            );
          }

          const receipt = await provider.waitForTransaction(hash, 1);

          if (receipt.status !== 1) {
            throw new Error("Transaction failed");
          }
        })
        .catch((error: any) => {
          errorTransaction(error, "associate failed");
          trackEvent(
            tokenType === TOKENTYPE.TOKEN
              ? TOKEN_ASSOCIATE_FAILED
              : TOKENX_ASSOCIATE_FAILED,
            {
              reason: getErrorMessage(error, "associate failed"),
            }
          );
        })
        .finally(() => {
          endTransaction();
        });
    }

    if (wcTopic == null) {
      return;
    }

    associateToken(walletAddress, signer, {
      topic: wcTopic,
      isNativeChain,
      connectorID,
    })
      .then(async (response: any) => {
        const res = await getFormatedResponse(response);
        if (res.success) {
          if (tokenType === TOKENTYPE.TOKENX) {
            dispatch(
              updateStakeData({
                tokenXAssociated: true,
              })
            );
          }
          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: res?.response.transactionHash || "",
          }));
        } else {
          setPersistantData((prev: any) => ({
            ...prev,
            ...getErrorData(res),
          }));
        }
      })
      .catch((error: any) => {
        errorTransaction(error, "associate failed");
        trackEvent(
          tokenType === TOKENTYPE.TOKEN
            ? TOKEN_ASSOCIATE_FAILED
            : TOKENX_ASSOCIATE_FAILED,
          {
            reason: getErrorMessage(error, "associate failed"),
          }
        );
      })
      .finally(() => {
        endTransaction();
      });
  };

  const handleApprove = async (transaction: any, tokenType: TOKENTYPE) => {
    startTransaction(TXN_TYPES.TOKEN_APPROVE);
    dispatch(
      updateStakeData({
        approveTokenLoading: tokenType === TOKENTYPE.TOKEN,
        approveTokenXLoading: tokenType === TOKENTYPE.TOKENX,
      })
    );

    const isUnderMaintainance = await checkMaintainance();
    if (isUnderMaintainance) {
      cancelTransaction();
      return;
    }

    if (tokenType === TOKENTYPE.TOKEN) {
      trackEvent(TOKEN_APPROVE_CTA, {
        stake_amount_entered: parseFloat(transaction.amount),
      });
    } else {
      trackEvent(TOKEN_APPROVE_CTA, {
        unstake_amount_entered: parseFloat(transaction.amount),
      });
    }

    if (connectorID === METAMASK) {
      const amount =
        Number(tokenType === TOKENTYPE.TOKEN ? stakeAmount : unstakeAmount) *
        1.1;

      approveHederaEvmToken(
        TokenId.fromString(config.ids.tokenId),
        ContractId.fromString(config.ids.stakingContractId),
        amount.toFixed(6)
      )
        .then(async (hash: string | null) => {
          if (hash == null) {
            throw new Error("Hash is null");
          }

          if (tokenType === TOKENTYPE.TOKENX) {
            dispatch(
              updateUserData({
                approvedTokenX: true,
              })
            );
          }
          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: hash,
          }));

          const provider = getProvider();

          if (!provider) {
            throw new Error(
              "[EDGECASE]: No provider found; should never happen"
            );
          }

          const receipt = await provider.waitForTransaction(hash, 1);

          if (receipt.status !== 1) {
            throw new Error("Transaction failed");
          }
        })
        .catch((error: any) => {
          errorTransaction(error, "approval failed");
          trackEvent(
            tokenType === TOKENTYPE.TOKEN
              ? TOKEN_APPROVE_FAILED
              : TOKENX_APPROVE_FAILED,
            {
              reason: getErrorMessage(error, "approval failed"),
            }
          );
        })
        .finally(() => {
          dispatch(
            updateStakeData({
              approveTokenLoading: false,
              approveTokenXLoading: false,
            })
          );
          endTransaction();
        });

      return;
    }

    if (wcTopic == null) {
      return;
    }

    approveToken(
      walletAddress,
      signer,
      tokenType === TOKENTYPE.TOKEN
        ? stakeAmount
        : (Number(tokenXAllowence) + Number(unstakeAmount)).toString(),
      tokenType,
      {
        topic: wcTopic,
        isNativeChain,
        connectorID,
      }
    )
      .then(async (response: any) => {
        const res = await getFormatedResponse(response);
        if (res.success) {
          if (tokenType === TOKENTYPE.TOKENX) {
            dispatch(
              updateUserData({
                approvedTokenX: true,
              })
            );
          }
          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: false,
            hash: res?.response.transactionHash || "",
          }));
        } else {
          setPersistantData((prev: any) => ({
            ...prev,
            hasUserDenied: res?.error?.includes("denied"),
            error: res?.error?.replace(/_/g, " ").toLowerCase(),
            hash: res?.response.transactionHash || "",
          }));
        }
      })
      .catch((error: any) => {
        errorTransaction(error, "approval failed");
        trackEvent(
          tokenType === TOKENTYPE.TOKEN
            ? TOKEN_APPROVE_FAILED
            : TOKENX_APPROVE_FAILED,
          {
            reason: getErrorMessage(error, "approval failed"),
          }
        );
      })
      .finally(() => {
        dispatch(
          updateStakeData({
            approveTokenLoading: false,
            approveTokenXLoading: false,
          })
        );
        endTransaction();
      });
  };

  const handleTransactions = (transaction: any) => {
    switch (transaction.type) {
      case TRANSACTION_TYPE.STAKE:
        return handleStake();
      case TRANSACTION_TYPE.UNSTAKE:
        return handleUnStake();
      case TRANSACTION_TYPE.WITHDRAW:
        return handleClaim(transaction);
      case TRANSACTION_TYPE.APPROVE_TOKEN:
        return handleApprove(transaction, TOKENTYPE.TOKEN);
      case TRANSACTION_TYPE.APPROVE_TOKEN_X:
        return handleApprove(transaction, TOKENTYPE.TOKENX);
      case TRANSACTION_TYPE.ASSOCIATE_TOKEN_X:
        return handleTokenAssociate(transaction, TOKENTYPE.TOKENX);
    }
  };

  const attachEventListners = (event: any) => {
    switch (event.name) {
      case EVENTS.HANDLE_CONNECT:
        if (event.data) {
          console.log("event.data", event.data);
          connect(event.data as WalletConnector);
        }
        break;

      case EVENTS.HANDLE_DISCONNECT:
        dispatch(clearWalletData());
        dispatch(clearUserData());
        dispatch(clearStakeData());
        disconnect();
        break;

      case EVENTS.HANDLE_TRANSACTION:
        if (event.data) {
          handleTransactions(event.data);
        }
        break;
    }
  };

  useEffect(() => {
    attachEventListners(emitter);
  }, [emitter]);

  const resetLatestTransactionInfo = () => {
    dispatch(
      updateStakeData({
        txnLoader: false,
        isStaking: false,
        isUnstaking: false,
      })
    );
    setPersistantData({ isTxnProcessing: false });
  };

  const handleTxViewOnEtherScan = () => {
    const url = `${config.blockExplorerURL}/transaction/${persistantData.hash}`;
    window.open(url, "_blank");
  };

  return (
    <>
      {txnLoader && (
        <TransactionModal
          isOpen={txnLoader}
          hash={persistantData.hash}
          isTxnProcessing={persistantData.isTxnProcessing}
          error={persistantData.error}
          daysToWaitForWithdraw={DAYS_TO_WAIT_FOR_WITHDRAW}
          hasUserDenied={persistantData.hasUserDenied}
          closeAlert={resetLatestTransactionInfo}
          handleTxView={handleTxViewOnEtherScan}
          transactionType={persistantData.transactionType}
          token={token}
          network={network}
          chainId={config.ids.chainId}
        />
      )}
    </>
  );
};

export default ServiceProvider;
